import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { PageHero } from "@/components/page-hero";
import { Section } from "@/components/section";
import { Reveal } from "@/components/reveal";
import { CtaBand } from "@/components/cta-band";
import { lcpPreload } from "@/lib/images";
import { FAQS } from "@/lib/site-data";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ | Custom Framing Questions Answered | Magnolia House" },
      {
        name: "description",
        content:
          "Cost, turnaround, archival framing, appointments and unusual pieces — common custom framing questions answered by Magnolia House in Fuquay-Varina, NC.",
      },
      {
        property: "og:title",
        content: "Custom Framing FAQ | Magnolia House",
      },
      {
        property: "og:description",
        content:
          "How pricing, timelines and archival framing actually work at a certified frame shop.",
      },
    ],
    links: [lcpPreload("frame-selection")],
  }),
  component: FaqPage,
});

function FaqPage() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <>
      <PageHero
        eyebrow="Questions"
        title={
          <>
            Everything people
            <br />
            <span className="amber-text italic">ask first.</span>
          </>
        }
        lead="Framing has its own vocabulary. Here is what it all means, in plain language."
        image="frame-selection"
        alt="Frame corner samples at Magnolia House"
      />

      <Section>
        <div className="mx-auto max-w-3xl divide-y divide-border border-y border-border">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={(i % 4) * 60}>
                <div>
                  <button
                    type="button"
                    id={`faq-q-${i}`}
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-a-${i}`}
                    className="flex w-full items-start justify-between gap-6 py-6 text-left"
                  >
                    <span
                      className={`display text-2xl transition-colors duration-500 sm:text-[1.75rem] ${
                        isOpen ? "text-primary" : "hover:text-primary"
                      }`}
                    >
                      {f.q}
                    </span>
                    <Plus
                      aria-hidden
                      className={`mt-1 size-4 shrink-0 text-[var(--terracotta)] transition-transform duration-500 ${
                        isOpen ? "rotate-45" : ""
                      }`}
                    />
                  </button>
                  <div
                    className="grid transition-[grid-template-rows,opacity] duration-700"
                    style={{
                      gridTemplateRows: isOpen ? "1fr" : "0fr",
                      opacity: isOpen ? 1 : 0,
                    }}
                  >
                    <div
                      id={`faq-a-${i}`}
                      role="region"
                      aria-labelledby={`faq-q-${i}`}
                      aria-hidden={!isOpen}
                      className="overflow-hidden"
                    >
                      <p className="max-w-2xl pb-7 text-[15px] leading-relaxed text-muted-foreground">
                        {f.a}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <CtaBand />
    </>
  );
}
